import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const parseArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_err) {
      return [];
    }
  }

  return [];
};

const toParagraphs = (value) => {
  if (!value) return [];

  return String(value)
    .split(/\n{2,}/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const renderTextWithMath = (text, keyPrefix = 'math') => {
  const input = String(text || '');
  const segments = [];
  const regex = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;
  let lastIndex = 0;
  let match;
  let index = 0;

  while ((match = regex.exec(input)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: input.slice(lastIndex, match.index) });
    }

    if (match[1] !== undefined) {
      segments.push({ type: 'block-math', value: match[1].trim() });
    } else {
      segments.push({ type: 'inline-math', value: (match[2] || '').trim() });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < input.length) {
    segments.push({ type: 'text', value: input.slice(lastIndex) });
  }

  return segments.map((segment) => {
    const key = `${keyPrefix}-${index++}`;

    if (segment.type === 'inline-math' && segment.value) {
      return (
        <code key={key} className="px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono text-[0.95em]">
          {segment.value}
        </code>
      );
    }

    if (segment.type === 'block-math' && segment.value) {
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <pre className="p-3 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-100 font-mono whitespace-pre-wrap">
            {segment.value}
          </pre>
        </div>
      );
    }

    return <React.Fragment key={key}>{segment.value}</React.Fragment>;
  });
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return 'You are not authorized to use bot context.';
  if (status === 404) return 'Question not found.';
  if (status === 500) return 'Server error. Please try again shortly.';
  return error?.response?.data?.error || error?.message || 'Unable to load bot context.';
};

const BotQuestionPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const stateMeta = location.state?.meta || {};
  const stateQuestion = location.state?.question || null;

  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const loadingMessages = [
    'Checking question context...',
    'Gathering similar questions across years...',
    'Preparing explanation...',
    'Almost ready...'
  ];

  const dashboardType = useMemo(() => {
    if (location.pathname.startsWith('/admin/dashboard')) return 'admin';
    if (location.pathname.startsWith('/teachers/dashboard')) return 'teacher';
    return 'student';
  }, [location.pathname]);

  const getReadPath = (questionId) => {
    if (dashboardType === 'admin') return `/admin/dashboard/past-questions/read/${questionId}`;
    if (dashboardType === 'teacher') return `/teachers/dashboard/past-questions/read/${questionId}`;
    return `/students/dashboard/past-questions/read/${questionId}`;
  };

  const params = useMemo(() => ({
    courseId: stateMeta.courseId || undefined,
    subject: stateMeta.subject || stateQuestion?.subject || undefined,
    topic: stateQuestion?.topic || stateMeta.topic || undefined,
    questionText: stateQuestion?.text || undefined
  }), [stateMeta, stateQuestion]);

  useEffect(() => {
    if (!loading) {
      setLoadingProgress(100);
      return undefined;
    }

    setLoadingProgress(0);
    setLoadingMessageIndex(0);

    const timer = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 95) return 95;
        const remaining = 95 - prev;
        const naturalStep = Math.max(1, Math.ceil(remaining / 7));
        const jitter = Math.floor(Math.random() * 3);
        const next = prev + naturalStep + jitter;
        return next > 95 ? 95 : next;
      });
    }, 240);

    const messageTimer = setInterval(() => {
      setLoadingMessageIndex((prev) => {
        if (prev >= loadingMessages.length - 1) return prev;
        return prev + 1;
      });
    }, 1200);

    return () => {
      clearInterval(timer);
      clearInterval(messageTimer);
    };
  }, [loading, loadingMessages.length]);

  useEffect(() => {
    let mounted = true;

    const fetchBotContext = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/questions/${id}/bot-context`, { params });
        if (!mounted) return;
        setPayload(res.data || null);
      } catch (err) {
        if (!mounted) return;
        setError(getErrorMessage(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) {
      fetchBotContext();
    }

    return () => {
      mounted = false;
    };
  }, [id, params]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Loading Question Bot</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-3">{loadingMessages[loadingMessageIndex]}</p>

          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden mb-2">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-500 transition-all duration-300"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>

          <p className="text-sm text-indigo-700 font-medium">{loadingProgress}%</p>
        </div>
      </div>
    );
  }

  if (error || !payload?.question) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-lg w-full text-center">
          <h2 className="text-lg sm:text-xl font-semibold text-red-700 mb-2">Question Bot Error</h2>
          <p className="text-sm sm:text-base text-gray-700 mb-6">{error || 'No bot data found for this question.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { question, explanation, similarQuestions = [] } = payload;
  const questionImages = parseArray(question.images);
  const questionOptions = parseArray(question.options);
  const explanationParagraphs = toParagraphs(explanation?.text || 'No explanation available yet.');
  const explanationSource = explanation?.source || 'rule-based';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-2 text-sm sm:text-base"
          >
            <span className="text-xl">←</span>
            <span>Back</span>
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 text-right">🤖 Question Bot</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-5 sm:space-y-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Current Question</h2>
            <span className="text-xs sm:text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-medium w-fit">
              {question.subject || 'General'} {question.topic ? `• ${question.topic}` : ''}
            </span>
          </div>

          {questionImages.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-3">
              {questionImages.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Question visual ${index + 1}`}
                  className="max-h-64 max-w-full object-contain border rounded-lg shadow-sm"
                />
              ))}
            </div>
          )}

          <p className="text-gray-800 text-base sm:text-lg leading-relaxed whitespace-pre-wrap mb-4">
            {renderTextWithMath(question.text, 'question-text')}
          </p>

          {questionOptions.length > 0 && (
            <div className="space-y-2">
              {questionOptions.map((opt, index) => (
                <div
                  key={index}
                  className={`p-3 sm:p-3.5 rounded-lg border ${opt === question.answer ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}
                >
                  <span className={`text-sm sm:text-base font-medium ${opt === question.answer ? 'text-green-700' : 'text-gray-700'}`}>{opt}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-indigo-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-purple-50 px-4 sm:px-6 py-4 border-b border-indigo-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Explanation</h3>
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-white text-indigo-700 border border-indigo-200 w-fit">
              {explanationSource}
            </span>
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            {explanationParagraphs.map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-7 sm:leading-8 text-[0.97rem] sm:text-[1.02rem]">
                {renderTextWithMath(paragraph, `explanation-${index}`)}
              </p>
            ))}

            <div className="pt-2">
              <p className="text-sm text-gray-500 italic">Tip: Compare this with the highlighted correct option above to lock in the concept.</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Similar Practice Questions</h3>

          {similarQuestions.length === 0 ? (
            <p className="text-gray-600">No similar questions found yet for this topic.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {similarQuestions.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <p className="text-xs sm:text-sm text-gray-500 mb-2">
                    {item.subject || 'General'} {item.topic ? `• ${item.topic}` : ''} {item.year ? `• ${item.year}` : ''}
                  </p>

                  {parseArray(item.images).length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {parseArray(item.images).slice(0, 2).map((src, index) => (
                        <img
                          key={`${item.id}-img-${index}`}
                          src={src}
                          alt={`Similar question visual ${index + 1}`}
                          className="max-h-28 max-w-full object-contain border rounded-md bg-white"
                        />
                      ))}
                    </div>
                  )}

                  <p className="text-sm sm:text-base text-gray-800 mb-4 line-clamp-3">{item.text}</p>
                  {/* Render possible answers for the similar question */}
                  {(() => {
                    const opts = parseArray(item.options);
                    if (!opts || opts.length === 0) return null;
                    return (
                      <div className="space-y-2 mb-4">
                        {opts.map((opt, idx) => (
                          <div
                            key={`${item.id}-opt-${idx}`}
                            className={`p-2 rounded-lg border text-sm ${opt === item.answer ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-200 text-gray-700'}`}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <button
                    onClick={() => navigate(getReadPath(item.id), { state: item })}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Open Question
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotQuestionPage;
